import React, { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../Auth/Context";
import { socketWex } from "../../utils/Socket";
import useWexClient from "../../hooks/Clients/wexClient,";
import {
  Stack,
  Box,
  Button,
  Typography,
  Avatar,
  IconButton,
  TextField,
  InputAdornment,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { Call, Search, PersonRounded } from "@mui/icons-material";
import Navbar from "../organisms/Navbar";
import BoxHeader from "../atoms/BoxHeader";
import stylesDashboard from "../../styles/Organisms/OpcionDashboard/dasboard";
import stylesReports from "../../styles/Organisms/OpcionesReportes/reports";
import MobileCallModal from "./MobileCallModal";

// ─── Tarjeta de agente ───────────────────────────────────────────────────────
function AgentCard({ agent, selected, onSelect, onCall }) {
  const agentName = agent.name || agent.alias || "Sin nombre";

  return (
    <Box
      onClick={() => onCall(agent)}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2,
        py: 1.5,
        cursor: "pointer",
        borderLeft: selected ? `4px solid #0c4a91` : "4px solid transparent",
        backgroundColor: selected ? "#e8f0fe" : "white",
        transition: "all 0.18s ease",
        "&:hover": { backgroundColor: selected ? "#e8f0fe" : "#f5f7ff" },
      }}
    >
      {/* Avatar */}
      <Avatar
        sx={{
          width: 44,
          height: 44,
          bgcolor: "#dde3f0",
          color: "#374151",
          fontFamily: "Raleway, sans-serif",
          fontWeight: 700,
          flexShrink: 0,
        }}
      >
        {agentName.charAt(0).toUpperCase()}
      </Avatar>

      {/* Info */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          noWrap
          sx={{
            fontFamily: "Raleway, sans-serif",
            fontWeight: 600,
            fontSize: "0.875rem",
            color: "#1a1a2e",
          }}
        >
          {agentName}
        </Typography>
        <Typography
          noWrap
          sx={{
            fontFamily: "Raleway, sans-serif",
            fontSize: "0.75rem",
            color: "#6b7280",
          }}
        >
          Destino: {agent.to || "N/A"}
        </Typography>
      </Box>

      {/* Botón llamar */}
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          onCall(agent);
        }}
        size="small"
        sx={{
          backgroundColor: "#4CAF50",
          color: "white",
          width: 36,
          height: 36,
          flexShrink: 0,
          "&:hover": { backgroundColor: "#388E3C" },
          transition: "background 0.18s",
        }}
      >
        <Call sx={{ fontSize: "1rem" }} />
      </IconButton>
    </Box>
  );
}

// ─── Panel derecho: detalle ──────────────────────────────────────────────────
function DetailPanel({ agent, onCall }) {
  if (!agent) {
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          color: "#9ca3af",
          p: 4,
        }}
      >
        <PersonRounded sx={{ fontSize: "4rem", color: "#d1d5db" }} />
        <Typography
          sx={{
            fontFamily: "Raleway, sans-serif",
            fontSize: "1rem",
            color: "#9ca3af",
          }}
        >
          Selecciona un agente para marcar
        </Typography>
      </Box>
    );
  }

  const agentName = agent.name || agent.alias || "Sin nombre";

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 3,
        p: { xs: 3, md: 6 },
        background: "linear-gradient(160deg, #f8faff 0%, #ffffff 100%)",
      }}
    >
      <Avatar
        sx={{
          width: 90,
          height: 90,
          bgcolor: "#dde3f0",
          color: "#374151",
          fontFamily: "Raleway, sans-serif",
          fontWeight: 700,
          fontSize: "2.5rem",
          boxShadow: "0 4px 20px rgba(12,74,145,0.15)",
        }}
      >
        {agentName.charAt(0).toUpperCase()}
      </Avatar>

      <Box sx={{ textAlign: "center" }}>
        <Typography
          sx={{
            fontFamily: "Raleway, sans-serif",
            fontWeight: 700,
            fontSize: "1.4rem",
            color: "#1a1a2e",
          }}
        >
          {agentName}
        </Typography>
        {agent.alias && (
          <Typography
            sx={{
              fontFamily: "Raleway, sans-serif",
              color: "#6b7280",
              fontSize: "0.9rem",
            }}
          >
            @{agent.alias}
          </Typography>
        )}
      </Box>

      <Divider sx={{ width: "80%", borderColor: "#e5e7eb" }} />

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          width: "100%",
          maxWidth: 340,
        }}
      >
        {[
          { label: "Número Destino (To)", value: agent.to || "N/A" },
          { label: "Línea Origen (From)", value: agent.from || "N/A" },
          { label: "Código", value: agent.code || "N/A" },
        ].map(({ label, value }) => (
          <Box
            key={label}
            sx={{ display: "flex", justifyContent: "space-between", px: 2 }}
          >
            <Typography
              sx={{
                fontFamily: "Raleway, sans-serif",
                fontSize: "0.82rem",
                color: "#9ca3af",
              }}
            >
              {label}
            </Typography>
            <Typography
              sx={{
                fontFamily: "Raleway, sans-serif",
                fontSize: "0.82rem",
                fontWeight: 600,
                color: "#374151",
              }}
            >
              {value}
            </Typography>
          </Box>
        ))}
      </Box>

      <IconButton
        onClick={() => onCall(agent)}
        sx={{
          backgroundColor: "#4CAF50",
          color: "white",
          width: 72,
          height: 72,
          boxShadow: "0 6px 24px rgba(76,175,80,0.4)",
          "&:hover": { backgroundColor: "#388E3C", transform: "scale(1.05)" },
          transition: "all 0.2s",
        }}
      >
        <Call sx={{ fontSize: "2rem" }} />
      </IconButton>

      <Typography
        sx={{
          fontFamily: "Raleway, sans-serif",
          fontSize: "0.78rem",
          color: "#9ca3af",
        }}
      >
        Toca para marcar
      </Typography>
    </Box>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────
function DashboardAgents() {
  const { payload } = useContext(AuthContext);

  const STORAGE_KEY = "dashboard-agents-disponibles";

  const [selectedAgent, setSelectedAgent] = useState(null);
  const [search, setSearch] = useState("");
  const [showDetail, setShowDetail] = useState(false);
  const [disponibleAgents, setDisponibleAgents] = useState(() => {
    try {
      const cached = sessionStorage.getItem(STORAGE_KEY);
      return cached ? JSON.parse(cached) : [];
    } catch {
      return [];
    }
  });
  const [callModalOpen, setCallModalOpen] = useState(false);
  const [callAgent, setCallAgent] = useState(null);

  // Persist to sessionStorage on every change
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(disponibleAgents));
    } catch {
      // quota exceeded or unavailable, ignore
    }
  }, [disponibleAgents]);

  const normalizePhone = (phone) => String(phone || "").replace(/\D/g, "");

  useEffect(() => {
    if (!payload) return;
    console.log("Payload:", payload);
    const userId = payload?._id;

    const joinCallUsers = () => {
      socketWex.emit("join-user-calls", { idUser: userId }, (response) => {
        if (!response?.success) {
          console.error("Error al unirse a call users:", response?.message);
        } else {
          console.log("Join call users exitoso");
        }
      });
    };
    const handleSocketDisconnect = (reason) => {
      console.warn("Socket desconectado:", reason);
    };

    const resetCallState = () => {
      setCallModalOpen(false);
      setCallAgent(null);
      setSelectedAgent(null);
      setShowDetail(false);
    };

    const handleCallStart = (data) => {
      console.log("Llamada iniciada/contestada:", data);

      const companyNumber = normalizePhone(data?.COMPANYNUMBER);
      const clientNumber = normalizePhone(data?.CLIENTNUMBER);
      /** SI Inicia la llamada el numero que llamamos lo compara
       * con la lista de disponibles y si coinciden quitar de disponibleAgents
       **/
      setDisponibleAgents((prev) =>
        prev.filter((agent) => {
          const agentFrom = normalizePhone(agent?.from);
          const agentTo = normalizePhone(agent?.to);

          const isSameAgent =
            agentFrom === companyNumber && agentTo === clientNumber;

          return !isSameAgent;
        }),
      );
    };

    const handleCallEnd = (data) => {
      console.log("Llamada finalizada:", data);
      resetCallState();
    };

    const handleAdjusterMarked = (data) => {
      const adjuster = {
        _id:
          data?.dataAdjuster?._id || data?.data?._id || Date.now().toString(),
        name: data?.dataAdjuster?.name || data?.dataAdjuster?.user || "",
        alias: data?.dataAdjuster?.alias || "",
        code: data?.dataAdjuster?.code || "",
        from: data?.from || data?.data?.from || "",
        to: data?.to || data?.data?.to || "",
      };

      setDisponibleAgents((prev) => {
        const index = prev.findIndex((agent) => agent._id === adjuster._id);

        if (index === -1) {
          return [...prev, adjuster];
        }

        const updated = [...prev];
        updated[index] = adjuster;

        return updated;
      });
    };

    // Sockets listeners
    socketWex.on("connect", joinCallUsers);
    socketWex.on("disconnect", handleSocketDisconnect);
    socketWex.on("user-call-started", handleCallStart);
    socketWex.on("user-call-ended", handleCallEnd);
    socketWex.on("adjuster-marked", handleAdjusterMarked);
    if (socketWex.connected) {
      joinCallUsers();
    } else {
      socketWex.connect();
    }
    return () => {
      socketWex.off("connect", joinCallUsers);
      socketWex.off("disconnect", handleSocketDisconnect);
      socketWex.off("adjuster-marked", handleAdjusterMarked);
      socketWex.off("user-call-started", handleCallStart);
      socketWex.off("user-call-ended", handleCallEnd);
    };
  }, [payload]);

  const filtered = disponibleAgents.filter((a) => {
    const q = search.toLowerCase();
    const nameMatch = a.name ? a.name.toLowerCase().includes(q) : false;
    const aliasMatch = a.alias ? a.alias.toLowerCase().includes(q) : false;
    const toMatch = a.to ? a.to.toLowerCase().includes(q) : false;

    return nameMatch || aliasMatch || toMatch;
  });

  const handleSelect = (agent) => {
    setSelectedAgent(agent);
    setShowDetail(true);
  };

  const handleCall = (agent) => {
    if (!agent) return;
    setCallAgent(agent);
    setCallModalOpen(true);
  };

  useEffect(() => {
    if (!disponibleAgents) return;
    console.log("disponibles", disponibleAgents);
  }, [disponibleAgents]);

  return (
    <Stack sx={{ width: "100vw", minHeight: "100vh", height: "auto" }}>
      {/* ── Navbar ── */}
      <Stack sx={{ width: "100%" }}>
        <Navbar />
      </Stack>

      {/* ── Header con gradiente ── */}
      <Stack sx={{ ...stylesReports.color, width: "100vw" }}>
        <BoxHeader>
          <Stack
            direction="row"
            sx={{
              alignItems: "center",
              justifyContent: "space-between",
              px: { xs: 4, md: 8 },
              py: 2,
            }}
          >
            <Box sx={{ top: "40px", display: "flex", flexDirection: "column" }}>
              <Typography
                variant="h5"
                sx={{
                  ...stylesDashboard.title,
                  fontSize: { xs: "1.3rem", md: "1.8rem" },
                  fontWeight: 700,
                  lineHeight: 1.2,
                }}
              >
                {"Bienvenido!" /* aqui puede ir el avatar*/}
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Raleway, sans-serif",
                  color: "rgba(255,255,255,0.75)",
                  fontSize: "0.82rem",
                  mt: 0.3,
                }}
              >
                Ajustadores marcados para llamada
              </Typography>
            </Box>
          </Stack>
        </BoxHeader>
      </Stack>

      {/* ── Cuerpo principal ── */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          height: { md: "calc(100vh - 120px)" },
          backgroundColor: "#f3f4f6",
          overflow: "hidden",
        }}
      >
        {/* Panel izquierdo: lista */}
        <Box
          sx={{
            width: { xs: "100%", md: "380px" },
            flexShrink: 0,
            display: { xs: showDetail ? "none" : "flex", md: "flex" },
            flexDirection: "column",
            backgroundColor: "white",
            borderRight: { md: "1px solid #e5e7eb" },
            height: { md: "100%" },
          }}
        >
          {/* Buscador */}
          {/*  <Box sx={{ p: 2, borderBottom: "1px solid #f0f0f0" }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Buscar por agente, teléfono..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: "#9ca3af", fontSize: "1.1rem" }} />
                  </InputAdornment>
                ),
                sx: { fontFamily: "Raleway, sans-serif", fontSize: "0.85rem" },
              }}
              sx={{ "& fieldset": { borderColor: "#e5e7eb" } }}
            />
          </Box>

          <Divider /> */}

          {/* Lista scrolleable */}
          <Box sx={{ flex: 1, overflowY: "auto" }}>
            {filtered.length === 0 ? (
              <Box sx={{ p: 4, textAlign: "center", color: "#9ca3af" }}>
                <Typography
                  sx={{
                    fontFamily: "Raleway, sans-serif",
                    fontSize: "0.85rem",
                  }}
                >
                  Sin agentes marcados
                </Typography>
              </Box>
            ) : (
              filtered.map((agent, idx) => (
                <React.Fragment key={agent._id || idx}>
                  <AgentCard
                    agent={agent}
                    selected={selectedAgent?._id === agent._id}
                    onSelect={handleSelect}
                    onCall={handleCall}
                  />
                  {idx < filtered.length - 1 && <Divider />}
                </React.Fragment>
              ))
            )}
          </Box>
        </Box>

        {/* Panel derecho: detalle */}
        <Box
          sx={{
            flex: 1,
            display: { xs: showDetail ? "flex" : "none", md: "flex" },
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Botón volver en mobile */}
          {showDetail && (
            <Box
              sx={{
                display: { md: "none" },
                px: 2,
                py: 1,
                backgroundColor: "white",
                borderBottom: "1px solid #e5e7eb",
              }}
            >
              <Typography
                onClick={() => setShowDetail(false)}
                sx={{
                  fontFamily: "Raleway, sans-serif",
                  fontSize: "0.85rem",
                  color: "#0c4a91",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                ← Volver a lista
              </Typography>
            </Box>
          )}

          <DetailPanel agent={selectedAgent} onCall={handleCall} />
        </Box>
      </Box>

      {/* Modal de llamada */}
      <MobileCallModal
        open={callModalOpen}
        onClose={() => {
          setCallModalOpen(false);
          setCallAgent(null);
          setSelectedAgent(null);
          setShowDetail(false);
        }}
        agentData={callAgent}
        payload={payload}
      />
    </Stack>
  );
}

export default DashboardAgents;
